import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BizzMineSessionStorageService } from 'src/app/shared/services/localStorage/bizzmine-session-storage.service';

@Injectable({
  providedIn: 'root'
})
export class SidebarService {

  private bizzSessionStorageKey = 'bizzSidebarUnpinned';
  private bizzSesssionStorage = inject(BizzMineSessionStorageService);

  private sidebarExpanded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private sidebarUnPinned: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private hoverTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly hoverOpenDelay = 180; // ms
  private sidebarBreakpoint = 800;
  public ignoreResize: Array<boolean> = [];
  public initialTaskState : {expanded: boolean, unpinned: boolean} | undefined = undefined;

  public set unpinned(unPinned: boolean) {
    this.sidebarUnPinned.next(unPinned);
  }

  public set expanded(expanded: boolean) {
    this.sidebarExpanded.next(expanded);
  }

  public get expanded(): boolean {
    return this.sidebarExpanded.getValue();
  }

  public get unpinned(): boolean {
    return this.sidebarUnPinned.getValue();
  }

  public pushIgnoreResize (): void {
    this.ignoreResize.push(true);
  }

  public popIgnoreResize (): void {
    if (this.ignoreResize.length > 0) {
      this.ignoreResize.pop();
      if (this.ignoreResize.length == 0) {
        if(this.initialTaskState != undefined){
          this.expanded = this.initialTaskState.expanded;
          this.unpinned = this.initialTaskState.unpinned;
          this.initialTaskState = undefined;
        }else {
          this.restoreIfUnpinned();
        }
      }
    }
  }

  public sidebarInit (): void {
    const storageKey = this.bizzSesssionStorage.get(this.bizzSessionStorageKey);
    if (storageKey == null) {
      // First time visit, we set the default based on screen size (Ipad = unpinned & collapsed)
      if (window.innerWidth <= this.sidebarBreakpoint){
        this.bizzSesssionStorage.set(this.bizzSessionStorageKey, true.toString());
      }else {
        this.bizzSesssionStorage.set(this.bizzSessionStorageKey, false.toString());
      }
    }
      this.restoreIfUnpinned();
  }

  public get unpinnedCached(): boolean | undefined {
    const storageKey = this.bizzSesssionStorage.get(this.bizzSessionStorageKey);
    if (storageKey !== null) {
      return storageKey.toLowerCase() == 'true';
    }else{
      return undefined;
    }
  }

  public restoreIfUnpinned(): void {
    if (this.ignoreResize.length > 0) {
      return;
    }
    if (this.unpinnedCached == null) {
      // User had the sidebar pinned, so we reset it to the default state
      this.resetSidebar();
    } else {
      this.unpinned = this.unpinnedCached;
      this.expanded = !this.unpinnedCached;
    }
  }

  public rememberState(): void {
    this.bizzSesssionStorage.set(this.bizzSessionStorageKey, this.unpinned.toString());
  }

  public toggleSidebar(force?: boolean): void {
    if(force == true || this.unpinnedCached == true) {
      this.expanded = !this.expanded;
      this.unpinned = !this.unpinned;
    }
  }

  public toggleUnPinSidebar(): void {
    if(this.initialTaskState) {
      this.initialTaskState = undefined;
    }
    this.unpinned = !this.unpinned;
  }

  public collapseSidebar(): void{
    // Cancel any pending open when leaving/blur to prevent late expand
    this.clearHoverTimer();
    if (this.expanded) {
      this.expanded = false;
    }
  }

  public collapseTaskSidebar(): void{
    // Cancel any pending open when leaving/blur to prevent late expand
    this.initialTaskState = this.initialTaskState ?? {expanded: this.expanded, unpinned: this.unpinned};
    this.expanded = false;
    this.unpinned = true;
  }

  public unCollapseSidebar(): void{
    // Debounced open: schedule once, cancel if the pointer leaves quickly
    this.clearHoverTimer();
    if (!this.expanded) {
      this.hoverTimeoutId = setTimeout(() => {
        this.expanded = true;
        this.hoverTimeoutId = null;
      }, this.hoverOpenDelay);
    }
  }


  // Utility to cancel any pending timers to prevent stale callbacks firing
  private clearHoverTimer(): void {
    if (this.hoverTimeoutId !== null) {
      clearTimeout(this.hoverTimeoutId);
      this.hoverTimeoutId = null;
    }
  }

  public resetSidebar(): void{
    this.expanded = true;
    this.unpinned = false;
  }

  public getObservable(): Observable<boolean> {
    return this.sidebarExpanded.asObservable();
  }
  public getUnpinnedObservable(): Observable<boolean> {
    return this.sidebarUnPinned.asObservable();
  }
}
